import { Player, ISdkParams, makeSafeForCSS } from "@blings/blings-player";
import editorTools from "@blings/blings-player/editor-tools";
import type {
  BlingsEditorTools,
  LayerTextBoxResizeEvent,
  LayerTextConnectorActionEvent,
  LayerTextControlReportRequest,
  LayerTextEditEvent,
  LayerTransformControlOptions,
  LayerTransformEvent,
  MotionLayerControlReport,
} from "@blings/blings-player/editor-tools";
import { recordStudioPerf, studioPerfNow } from "./studioPerfDebug";

export type LayerInspectorSelection = {
  path: string;
  instancePath?: string;
  className?: string;
  assetName?: string;
  layerName?: string;
  layerUid?: number;
  layerType?: number;
  refId?: string;
  assetUid?: number;
};

export type LayerInspectorMode = "view" | "design" | "connect";

export type LayerInspectorState = {
  available: boolean;
  enabled: boolean;
  mode: LayerInspectorMode;
  attached: boolean;
  selections: LayerInspectorSelection[];
  primarySelection?: LayerInspectorSelection;
  hoveredLayers: LayerInspectorSelection[];
  hoveredPrimarySelection?: LayerInspectorSelection;
  contextMenu?: LayerInspectorContextMenuEvent;
  contextMenuDismissId?: number;
};

export type LayerInspectorContextMenuEvent = {
  id: number;
  position: {
    clientX: number;
    clientY: number;
    pageX: number;
    pageY: number;
  };
  layer?: LayerInspectorSelection;
  selections?: LayerInspectorSelection[];
};

type LayerInspectorTransformCommitHandler = (
  event: LayerTransformEvent
) => void;
type LayerInspectorTextControlReportHandler = (
  request: LayerTextControlReportRequest
) =>
  | MotionLayerControlReport
  | undefined
  | Promise<MotionLayerControlReport | undefined>;
type LayerInspectorTextEditCommitHandler = (
  event: LayerTextEditEvent | LayerTextBoxResizeEvent
) => void;
type LayerInspectorTextConnectorActionHandler = (
  event: LayerTextConnectorActionEvent
) => void;
type LayerInspectorLayerDoubleClickHandler = (
  selection: LayerInspectorSelection
) => void;
type LayerInspectorSubscriber = (state: LayerInspectorState) => void;
type LayerInspectorSubscriberMeta = {
  id: number;
  label: string;
  source?: string;
};
type LayerInspectorPluginOptions = NonNullable<
  Parameters<BlingsEditorTools["inspectorAPI"]["createLayerInspectorPlugin"]>[0]
>;
type LayerOutlineStyleOptions = NonNullable<
  LayerInspectorPluginOptions["selectionOutline"]
>;

type LayerSelectionQuery = {
  path?: string;
  className?: string;
  layerName?: string;
  assetName?: string;
};

const buildLayerUidClassName = (
  layerUid: number,
  assetName?: string
): string => {
  const safeLayerName = makeSafeForCSS(`uid_${layerUid}`);
  const safeAssetName = assetName ? makeSafeForCSS(assetName) : undefined;
  return `bl_edit_selector-${
    safeAssetName
      ? `$a_${safeAssetName}_$l_${safeLayerName}`
      : `$l_${safeLayerName}`
  }`;
};

const buildLayerNameClassName = (
  layerName: string,
  assetName?: string
): string => {
  const safeLayerName = makeSafeForCSS(layerName);
  const safeAssetName = assetName ? makeSafeForCSS(assetName) : undefined;
  return `bl_edit_selector-${
    safeAssetName
      ? `$a_${safeAssetName}_$l_${safeLayerName}`
      : `$l_${safeLayerName}`
  }`;
};

const CONNECT_OUTLINE_STYLE: LayerOutlineStyleOptions = {
  borderStyle: "dotted",
  borderWidth: 1,
  color: "rgba(255, 88, 121, 0.5)",
  extraStyles: {
    background: "rgba(255, 88, 121, 0.04)",
  },
};

const CONNECT_OUTLINE_HOVER_STYLE: LayerOutlineStyleOptions = {
  ...CONNECT_OUTLINE_STYLE,
  extraStyles: { background: "none" },
};

const getSubscriptionSource = (): string | undefined => {
  const stack = new Error().stack;
  if (!stack) return undefined;
  return stack
    .split("\n")
    .slice(3, 7)
    .map((line) => line.trim())
    .join(" <- ");
};

const areLayerInspectorSelectionsEqual = (
  first?: LayerInspectorSelection,
  second?: LayerInspectorSelection
): boolean => {
  if (first === second) return true;
  if (!first || !second) return false;

  const hasStableIdentity =
    Boolean(first.path || second.path) ||
    Boolean(first.instancePath || second.instancePath) ||
    Boolean(first.className || second.className) ||
    Boolean(first.layerName || second.layerName) ||
    first.layerUid !== undefined ||
    second.layerUid !== undefined ||
    Boolean(first.refId || second.refId) ||
    first.assetUid !== undefined ||
    second.assetUid !== undefined;

  if (!hasStableIdentity) return false;

  return (
    first.path === second.path &&
    first.instancePath === second.instancePath &&
    first.className === second.className &&
    first.assetName === second.assetName &&
    first.layerName === second.layerName &&
    first.layerUid === second.layerUid &&
    first.layerType === second.layerType &&
    first.refId === second.refId &&
    first.assetUid === second.assetUid
  );
};

const areLayerInspectorSelectionListsEqual = (
  first: LayerInspectorSelection[],
  second: LayerInspectorSelection[]
): boolean =>
  first.length === second.length &&
  first.every((selection, index) =>
    areLayerInspectorSelectionsEqual(selection, second[index])
  );

const areLayerInspectorContextMenusEqual = (
  first?: LayerInspectorContextMenuEvent,
  second?: LayerInspectorContextMenuEvent
): boolean => {
  if (first === second) return true;
  if (!first || !second) return false;
  return first.id === second.id;
};

const areLayerInspectorStatesEqual = (
  first: LayerInspectorState,
  second: LayerInspectorState
): boolean =>
  first.available === second.available &&
  first.enabled === second.enabled &&
  first.mode === second.mode &&
  first.attached === second.attached &&
  first.contextMenuDismissId === second.contextMenuDismissId &&
  areLayerInspectorContextMenusEqual(first.contextMenu, second.contextMenu) &&
  areLayerInspectorSelectionListsEqual(first.selections, second.selections) &&
  areLayerInspectorSelectionsEqual(
    first.primarySelection,
    second.primarySelection
  ) &&
  areLayerInspectorSelectionListsEqual(
    first.hoveredLayers,
    second.hoveredLayers
  ) &&
  areLayerInspectorSelectionsEqual(
    first.hoveredPrimarySelection,
    second.hoveredPrimarySelection
  );

const getLayerInspectorSelectionDebugKey = (
  selection?: LayerInspectorSelection
): string => {
  if (!selection) return "";
  if (selection.instancePath) return `instance:${selection.instancePath}`;
  if (typeof selection.layerUid === "number") return `uid:${selection.layerUid}`;
  if (selection.path) return `path:${selection.path}`;
  if (selection.className) return `class:${selection.className}`;
  if (selection.layerName) {
    return `name:${selection.assetName ?? "main"}:${selection.layerName}`;
  }
  return "";
};

const getLayerInspectorStateDiff = (
  previousState: LayerInspectorState | undefined,
  nextState: LayerInspectorState
): string => {
  if (!previousState) return "initial";

  const changedFields: string[] = [];
  if (previousState.available !== nextState.available) {
    changedFields.push("available");
  }
  if (previousState.enabled !== nextState.enabled) {
    changedFields.push("enabled");
  }
  if (previousState.mode !== nextState.mode) {
    changedFields.push("mode");
  }
  if (previousState.attached !== nextState.attached) {
    changedFields.push("attached");
  }
  if (
    !areLayerInspectorSelectionListsEqual(
      previousState.selections,
      nextState.selections
    ) ||
    !areLayerInspectorSelectionsEqual(
      previousState.primarySelection,
      nextState.primarySelection
    )
  ) {
    changedFields.push("selection");
  }
  if (
    !areLayerInspectorSelectionListsEqual(
      previousState.hoveredLayers,
      nextState.hoveredLayers
    ) ||
    !areLayerInspectorSelectionsEqual(
      previousState.hoveredPrimarySelection,
      nextState.hoveredPrimarySelection
    )
  ) {
    changedFields.push("hover");
  }
  if (
    !areLayerInspectorContextMenusEqual(
      previousState.contextMenu,
      nextState.contextMenu
    )
  ) {
    changedFields.push("contextMenu");
  }
  if (previousState.contextMenuDismissId !== nextState.contextMenuDismissId) {
    changedFields.push("contextMenuDismissId");
  }

  return changedFields.join(",") || "unknown";
};

const getLayerInspectorHoverTransition = (
  previousHoverKey: string,
  nextHoverKey: string
): string => {
  if (previousHoverKey === nextHoverKey) return "same";
  if (!previousHoverKey && nextHoverKey) return "empty-to-layer";
  if (previousHoverKey && !nextHoverKey) return "layer-to-empty";
  return "layer-to-layer";
};

class LayerInspectorTools {
  private bundlePromise: Promise<void> | null = null;
  private editorTools: BlingsEditorTools | null = null;
  private plugin; //: any | null = null;
  private pluginMode?: LayerInspectorMode;
  private controller: any | null = null;
  private latestPlayer: Player | null = null;
  private enabled = true;
  private selectionCaptureActive = false;
  private mode: LayerInspectorMode = "connect";
  private subscribers = new Map<
    LayerInspectorSubscriber,
    LayerInspectorSubscriberMeta
  >();
  private subscriberCounter = 0;
  private selectionSummaries: LayerInspectorSelection[] = [];
  private primarySelection?: LayerInspectorSelection;
  private hoveredSummaries: LayerInspectorSelection[] = [];
  private hoveredPrimarySelection?: LayerInspectorSelection;
  private pendingContextMenu?: LayerInspectorContextMenuEvent;
  private contextMenuCounter = 0;
  private pendingContextMenuDismissId?: number;
  private contextMenuDismissCounter = 0;
  private publishedContextMenuOpen = false;
  private lastNotifiedState?: LayerInspectorState;
  private programmaticHoverKey?: string;
  private preservingSelectionsDuringTeardown = false;
  private clearingControllerInteractionState = false;
  private transformCommitHandler?: LayerInspectorTransformCommitHandler;
  private textControlReportHandler?: LayerInspectorTextControlReportHandler;
  private textEditCommitHandler?: LayerInspectorTextEditCommitHandler;
  private textConnectorActionHandler?: LayerInspectorTextConnectorActionHandler;
  private layerDoubleClickHandler?: LayerInspectorLayerDoubleClickHandler;

  // Drill-through state: track layers at last click point for cycling
  private drillThroughLayers: LayerInspectorSelection[] = [];
  private drillThroughIndex = 0;
  private lastDrillClickPosition: { x: number; y: number } | null = null;

  public async install(params: ISdkParams): Promise<void> {
    try {
      await this.loadBundle();
      this.ensurePlugin();
      this.plugin?.install?.(params);
      this.notify();
    } catch (error) {
      console.error("[LayerInspectorTools] Failed to install inspector", error);
    }
  }

  public attach(player: Player) {
    this.latestPlayer = player;
    this.teardownController({ preserveSelections: true });

    if (!this.plugin || this.pluginMode !== this.mode) {
      this.createPluginForCurrentMode();
    }
    if (!this.plugin) return null;

    this.controller = this.plugin.attach(player);

    this.applyEnabledState();
    if (
      this.preservingSelectionsDuringTeardown &&
      this.selectionSummaries.length
    ) {
      this.setSelections(
        this.selectionSummaries.map((selection) => ({ ...selection })),
        { clearExisting: true }
      );
    } else {
      this.syncSelectionsFromController();
      this.notify();
      this.preservingSelectionsDuringTeardown = false;
    }
    return this.controller;
  }

  public detach(): void {
    this.teardownController({ preserveSelections: true });
    this.latestPlayer = null;
    this.hoveredSummaries = [];
    this.hoveredPrimarySelection = undefined;
    this.notify();
  }

  public detachForPlayer(player?: Player | null): void {
    if (player && this.latestPlayer !== player) return;
    this.detach();
  }

  public setEnabled(enabled: boolean): void {
    this.setMode(enabled ? "design" : "view");
  }

  public setSelectionCaptureEnabled(enabled: boolean): void {
    const changed = this.selectionCaptureActive !== enabled;
    this.selectionCaptureActive = enabled;
    this.applyEnabledState();
    if (changed && !this.isControllerInteractionEnabled()) {
      this.notify();
    }
  }

  public toggle(): void {
    this.setEnabled(!this.enabled);
  }

  public setMode(mode: LayerInspectorMode): void {
    const changed = this.mode !== mode;
    this.mode = mode;
    this.enabled = mode !== "view";
    if (!this.enabled) {
      this.clearControllerInteractionState();
      this.clearLocalInteractionState();
    }
    if (changed && this.enabled && this.controller && this.latestPlayer) {
      this.reattachControllerForCurrentMode();
      return;
    }
    this.applyEnabledState();
    if (changed) {
      this.notify();
    }
  }

  public getMode(): LayerInspectorMode {
    return this.mode;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public isAvailable(): boolean {
    return Boolean(this.plugin);
  }

  public selectLayerByUid(
    layerUid: number,
    assetId?: string,
    assetName?: string,
    layerNm?: string,
    layerPath?: string
  ): void {
    if (!this.controller || !this.enabled) return;

    const matchingClassNames = this.findLayerClassNamesByUid(layerUid);
    const domClassName = matchingClassNames[0];

    // Try using className first as it's more specific
    // Format: bl_edit_selector-$a_<asset_id>_$l_uid_XXXXX (uses assetId, not assetName)
    const className = buildLayerUidClassName(layerUid, assetName ?? assetId);
    const selectionSummary: LayerInspectorSelection = {
      path: layerPath ?? "",
      className: domClassName ?? className,
      assetName: assetName ?? assetId,
      layerName: layerNm ?? `uid_${layerUid}`,
      layerUid,
    };

    if (domClassName) {
      if (typeof this.controller.selectByClassName === "function") {
        this.controller.selectByClassName(domClassName);
      } else {
        this.controller.select?.({ className: domClassName });
      }
      this.refreshSelectionsAfterProgrammaticSelect(selectionSummary);
      return;
    }

    if (layerPath) {
      const query: LayerSelectionQuery = { path: layerPath };
      this.controller.select?.(query);
    }

    let result = this.controller.select?.({ className });

    // Try with uid_ format layerName
    if (!result) {
      const layerName = `uid_${layerUid}`;
      const query: LayerSelectionQuery = { layerName };
      if (assetName) {
        query.assetName = assetName;
      }
      result = this.controller.select?.(query);
    }

    // Try with actual layer name (nm field)
    if (!result && layerNm) {
      const query: LayerSelectionQuery = { layerName: layerNm };
      if (assetName) {
        query.assetName = assetName;
      }
      result = this.controller.select?.(query);
    }

    // Try selectByLayerName method directly
    if (!result && layerNm) {
      result = this.controller.selectByLayerName?.(layerNm, assetName);
    }

    this.refreshSelectionsAfterProgrammaticSelect(selectionSummary);
  }

  public selectLayer(layerName: string, assetName?: string): void {
    if (!this.controller || !this.enabled) return;

    const query: LayerSelectionQuery = { layerName };
    if (assetName) {
      query.assetName = assetName;
    }
    this.controller.select?.(query);
  }

  public clearSelections(): void {
    this.clearControllerInteractionState();
    this.clearLocalInteractionState();
    this.notify();
  }

  public setHover(selection?: LayerInspectorSelection): void {
    const startedAt = studioPerfNow();
    if (!selection) {
      this.clearHover();
      recordStudioPerf(
        "LayerInspectorTools.setHover empty",
        studioPerfNow() - startedAt,
        { enabled: this.enabled, hasController: Boolean(this.controller) }
      );
      return;
    }

    if (!this.controller || !this.enabled) {
      recordStudioPerf(
        "LayerInspectorTools.setHover skipped",
        studioPerfNow() - startedAt,
        { enabled: this.enabled, hasController: Boolean(this.controller) }
      );
      return;
    }

    let queries = this.buildSelectionQueries(selection);
    const hoverKey = getLayerInspectorSelectionDebugKey(selection);

    if (typeof selection.layerUid === "number") {
      const domClassQueries = this.findLayerClassNamesByUid(
        selection.layerUid
      ).map((className) => ({ className }));
      queries = [...domClassQueries, ...queries];
    }

    const seenQueries = new Set<string>();
    const uniqueQueries = queries.filter((query) => {
      const key = JSON.stringify(query);
      if (seenQueries.has(key)) {
        return false;
      }
      seenQueries.add(key);
      return true;
    });

    for (let index = 0; index < uniqueQueries.length; index += 1) {
      const query = uniqueQueries[index];
      const result =
        index === uniqueQueries.length - 1
          ? this.controller.setHover?.(query) ?? this.controller.hover?.(query)
          : this.controller.hover?.(query) ?? this.controller.setHover?.(query);
      if (result) {
        this.programmaticHoverKey = hoverKey || undefined;
        this.hoveredSummaries = [{ ...selection }];
        this.hoveredPrimarySelection = { ...selection };
        this.notify();
        recordStudioPerf(
          "LayerInspectorTools.setHover hit",
          studioPerfNow() - startedAt,
          {
            layerUid: selection.layerUid,
            queryCount: uniqueQueries.length,
            queryIndex: index,
          }
        );
        return;
      }
    }

    this.clearHover();
    recordStudioPerf(
      "LayerInspectorTools.setHover miss",
      studioPerfNow() - startedAt,
      {
        layerUid: selection.layerUid,
        queryCount: uniqueQueries.length,
      }
    );
  }

  public clearHover(): void {
    const startedAt = studioPerfNow();
    const hadHover = Boolean(this.hoveredPrimarySelection);
    this.programmaticHoverKey = undefined;
    this.controller?.clearHover?.();
    this.hoveredSummaries = [];
    this.hoveredPrimarySelection = undefined;
    this.notify();
    recordStudioPerf(
      "LayerInspectorTools.clearHover",
      studioPerfNow() - startedAt,
      { hadHover, hasController: Boolean(this.controller) }
    );
  }

  public getHover(): LayerInspectorSelection | undefined {
    return this.hoveredPrimarySelection
      ? { ...this.hoveredPrimarySelection }
      : undefined;
  }

  public setTransformCommitHandler(
    handler?: LayerInspectorTransformCommitHandler
  ): void {
    this.transformCommitHandler = handler;
  }

  public setTextControlReportHandler(
    handler?: LayerInspectorTextControlReportHandler
  ): void {
    this.textControlReportHandler = handler;
  }

  public setTextEditCommitHandler(
    handler?: LayerInspectorTextEditCommitHandler
  ): void {
    this.textEditCommitHandler = handler;
  }

  public setTextConnectorActionHandler(
    handler?: LayerInspectorTextConnectorActionHandler
  ): void {
    this.textConnectorActionHandler = handler;
  }

  public setLayerDoubleClickHandler(
    handler?: LayerInspectorLayerDoubleClickHandler
  ): void {
    this.layerDoubleClickHandler = handler;
  }

  public setSelections(
    selections: LayerInspectorSelection[],
    options?: { clearExisting?: boolean }
  ): void {
    if (!this.controller || !this.enabled) return;
    const clearExisting = options?.clearExisting !== false;

    if (clearExisting) {
      this.controller.clearSelections?.();
    }

    const validSelections = selections.filter(
      (selection) => this.buildSelectionQueries(selection).length > 0
    );
    if (!validSelections.length) {
      this.selectionSummaries = [];
      this.primarySelection = undefined;
      this.preservingSelectionsDuringTeardown = false;
      this.notify();
      return;
    }

    validSelections.forEach((selection) => {
      let queries = this.buildSelectionQueries(selection);

      // For uid-based selections, try to find actual DOM className first
      // This matches the approach used in selectLayerByUid()
      if (typeof selection.layerUid === "number") {
        const domClassNames = this.findLayerClassNamesByUid(selection.layerUid);
        if (domClassNames.length > 0) {
          // Prepend actual DOM className as highest priority query
          queries = [{ className: domClassNames[0] }, ...queries];
        }
      }

      for (const query of queries) {
        const result = this.controller?.select?.(query);
        if (result) {
          break;
        }
      }
    });

    this.refreshSelectionsAfterProgrammaticMultiSelect(validSelections);
    if (this.preservingSelectionsDuringTeardown) {
      this.finishSelectionPreservation();
    }
  }

  public getState(): LayerInspectorState {
    return {
      available: this.isAvailable(),
      enabled: this.enabled,
      mode: this.mode,
      attached: Boolean(this.controller),
      selections: this.selectionSummaries.map((selection) => ({
        ...selection,
      })),
      primarySelection: this.primarySelection
        ? { ...this.primarySelection }
        : undefined,
      hoveredLayers: this.hoveredSummaries.map((selection) => ({
        ...selection,
      })),
      hoveredPrimarySelection: this.hoveredPrimarySelection
        ? { ...this.hoveredPrimarySelection }
        : undefined,
      contextMenu: this.pendingContextMenu
        ? {
            ...this.pendingContextMenu,
            layer: this.pendingContextMenu.layer
              ? { ...this.pendingContextMenu.layer }
              : undefined,
            selections: this.pendingContextMenu.selections
              ? this.pendingContextMenu.selections.map((selection) => ({
                  ...selection,
                }))
              : undefined,
            position: { ...this.pendingContextMenu.position },
          }
        : undefined,
      contextMenuDismissId: this.pendingContextMenuDismissId,
    };
  }

  public subscribe(
    cb: LayerInspectorSubscriber,
    debugLabel = "anonymous"
  ): () => void {
    const subscriber = {
      id: ++this.subscriberCounter,
      label: `${debugLabel}#${this.subscriberCounter}`,
      source: getSubscriptionSource(),
    };
    this.subscribers.set(cb, subscriber);
    const startedAt = studioPerfNow();
    const snapshot = this.getState();
    cb(snapshot);
    this.lastNotifiedState = snapshot;
    recordStudioPerf(
      `LayerInspectorTools.subscribe ${subscriber.label}`,
      studioPerfNow() - startedAt,
      { source: subscriber.source, subscribers: this.subscribers.size }
    );
    return () => {
      this.subscribers.delete(cb);
    };
  }

  private async loadBundle(): Promise<void> {
    if (this.editorTools || window.BlingsEditorTools) {
      this.editorTools = window.BlingsEditorTools ?? this.editorTools;
      return;
    }

    if (this.bundlePromise) {
      await this.bundlePromise;
      return;
    }

    this.bundlePromise = Promise.resolve()
      .then(() => {
        this.editorTools = editorTools;
        window.BlingsEditorTools = editorTools;
      })
      .finally(() => {
        this.bundlePromise = null;
      });

    await this.bundlePromise;
  }

  private ensurePlugin(): void {
    const tools = this.editorTools ?? window.BlingsEditorTools;
    if (!tools?.inspectorAPI) return;
    if (this.plugin && this.pluginMode === this.mode) return;
    if (this.controller) return;

    this.createPluginForCurrentMode();
  }

  private createPluginForCurrentMode(): void {
    const tools = this.editorTools ?? window.BlingsEditorTools;
    if (!tools?.inspectorAPI) return;

    this.plugin = tools.inspectorAPI.createLayerInspectorPlugin(
      this.buildInspectorPluginOptions()
    );
    this.pluginMode = this.mode;
  }

  private buildInspectorPluginOptions(): LayerInspectorPluginOptions {
    const connectOutlineOptions =
      this.mode === "connect"
        ? {
            hoverOutline: CONNECT_OUTLINE_HOVER_STYLE,
            selectionOutline: CONNECT_OUTLINE_STYLE,
            showLayerOutlineInfo: false,
          }
        : {};

    return {
      // enabled: false,
      deselectOnOutsideClick: true,
      persistentSelection: true,
      multiSelect: true,
      transformControls:
        this.mode === "design"
          ? this.buildTransformControlOptions()
          : this.buildConnectControlOptions(),
      ...connectOutlineOptions,
      onSelect: (selection, selections, event?: MouseEvent) => {
        const startedAt = studioPerfNow();
        const selectionCount = Array.isArray(selections)
          ? selections.length
          : 0;
        let outcome = "handled";
        try {
          if (this.clearingControllerInteractionState) {
            outcome = "skipped-clearing";
            return;
          }

          if (!this.isControllerInteractionEnabled()) {
            outcome = "skipped-disabled";
            this.clearDisabledInteractionState({ dismissContextMenu: true });
            return;
          }

          if (
            this.preservingSelectionsDuringTeardown &&
            (!Array.isArray(selections) || selections.length === 0)
          ) {
            outcome = "skipped-preserve-empty";
            return;
          }

          // Handle drill-through: Cmd+Option+Click cycles through overlapping layers
          if (event && event.metaKey && event.altKey) {
            outcome = "drill-through";
            this.handleDrillThroughClick(selection, selections, event);
            return;
          }

          // Reset drill-through state on normal clicks
          this.resetDrillThroughState();

          if (Array.isArray(selections)) {
            this.updateSelectionSnapshots(selections);
          } else {
            this.updateSelectionSnapshots([]);
          }

          // Shift is the only additive selection gesture. Cmd/Ctrl is reserved
          // by the layer inspector for cycling through stacked layers.
          if (
            event &&
            !event.shiftKey &&
            Array.isArray(selections) &&
            selections.length > 1
          ) {
            outcome = "collapse-multi";
            const summary = this.toSelectionSummary(selection);
            const query = this.buildSelectionQuery(summary);
            const controllerStartedAt = studioPerfNow();
            this.clearingControllerInteractionState = true;
            if (query) {
              try {
                this.controller?.clearSelections?.();
                this.controller?.select?.(query);
              } finally {
                this.clearingControllerInteractionState = false;
              }
              recordStudioPerf(
                "LayerInspectorTools.onSelect collapse-multi controller",
                studioPerfNow() - controllerStartedAt,
                { mode: this.mode, selectionCount }
              );
              this.refreshSelectionsAfterProgrammaticSelect(summary);
            } else {
              this.clearingControllerInteractionState = false;
              this.updateSelectionSnapshots([]);
              this.notify();
            }
            return;
          }

          if (!event || event.button !== 2) {
            this.requestContextMenuDismissal();
          }
          this.notify();
        } finally {
          recordStudioPerf(
            `LayerInspectorTools.onSelect ${outcome}`,
            studioPerfNow() - startedAt,
            {
              altKey: Boolean(event?.altKey),
              button: event?.button,
              metaKey: Boolean(event?.metaKey),
              mode: this.mode,
              selectionCount,
              shiftKey: Boolean(event?.shiftKey),
            }
          );
        }
      },
      onHover: (layers: unknown) => {
        const startedAt = studioPerfNow();
        const layerCount = Array.isArray(layers) ? layers.length : 0;
        const incomingHover =
          Array.isArray(layers) && layers.length
            ? this.toLayerInfoSummary(layers[0])
            : undefined;
        const incomingHoverKey =
          getLayerInspectorSelectionDebugKey(incomingHover);
        let outcome = "handled";
        let previousHoverKey = getLayerInspectorSelectionDebugKey(
          this.hoveredPrimarySelection
        );
        let nextHoverKey = previousHoverKey;
        try {
          if (this.clearingControllerInteractionState) {
            outcome = "skipped-clearing";
            return;
          }

          if (!this.isControllerInteractionEnabled()) {
            outcome = "skipped-disabled";
            this.clearDisabledInteractionState();
            return;
          }

          if (this.programmaticHoverKey) {
            outcome = "skipped-programmatic";
            nextHoverKey = this.programmaticHoverKey;
            return;
          }

          if (incomingHover) {
            this.hoveredSummaries = [incomingHover];
            this.hoveredPrimarySelection = incomingHover;
          } else {
            this.updateHoveredSnapshots([]);
          }
          nextHoverKey = getLayerInspectorSelectionDebugKey(
            this.hoveredPrimarySelection
          );
          this.notify();
        } finally {
          const transition = getLayerInspectorHoverTransition(
            previousHoverKey,
            nextHoverKey
          );
          recordStudioPerf(
            `LayerInspectorTools.onHover ${outcome} ${transition}`,
            studioPerfNow() - startedAt,
            {
              incomingHoverKey,
              layerCount,
              mode: this.mode,
              nextHoverKey,
              previousHoverKey,
            }
          );
        }
      },
      onRightClick: (layer, selections, event) => {
        this.handleRightClick(
          layer,
          selections,
          event as MouseEvent | undefined
        );
      },
      ...(this.mode === "connect"
        ? {
            onDoubleClick: (layer, selections) => {
              this.handleDoubleClick(layer, selections);
            },
          }
        : {}),
    };
  }

  private buildTransformControlOptions(): LayerTransformControlOptions {
    return {
      enabled: true,
      allowMove: true,
      allowScale: true,
      allowRotate: true,
      lockAspectRatio: true,
      restorePreviewOnCommit: false,
      allowTextEdit: true,
      onTransformCommit: (event: LayerTransformEvent) => {
        if (!event.changes.length) return;
        this.transformCommitHandler?.(event);
      },
      getTextControlReport: (request: LayerTextControlReportRequest) =>
        this.textControlReportHandler?.(request),
      onTextEditCommit: (event: LayerTextEditEvent) => {
        if (!event.changes.length) return;
        this.textEditCommitHandler?.(event);
      },
      onTextConnectorAction: (event: LayerTextConnectorActionEvent) => {
        this.textConnectorActionHandler?.(event);
      },
      onTextBoxResizeCommit: (event: LayerTextBoxResizeEvent) => {
        if (!event.changes.length) return;
        this.textEditCommitHandler?.(event);
      },
    };
  }

  private buildConnectControlOptions(): LayerTransformControlOptions {
    return {
      ...this.buildTransformControlOptions(),
      enabled: false,
      allowMove: false,
      allowScale: false,
      allowRotate: false,
      allowTextEdit: false,
    };
  }

  private applyEnabledState(): void {
    if (!this.controller) return;

    const interactionEnabled = this.isControllerInteractionEnabled();
    const transformControlsEnabled = this.enabled && this.mode === "design";

    if (!transformControlsEnabled) {
      this.controller.setTransformControlsEnabled?.(
        false,
        this.mode === "connect" ? this.buildConnectControlOptions() : undefined
      );
      if (!interactionEnabled) {
        this.clearControllerInteractionState();
      }
    } else {
      this.controller.setTransformControlsEnabled?.(
        true,
        this.buildTransformControlOptions()
      );
    }

    if (!interactionEnabled) {
      this.clearLocalInteractionState();
    }

    if (typeof this.controller.setEnabled === "function") {
      this.controller.setEnabled(interactionEnabled);
      return;
    }

    if (interactionEnabled && typeof this.controller.enable === "function") {
      this.controller.enable();
    } else if (
      !interactionEnabled &&
      typeof this.controller.disable === "function"
    ) {
      this.controller.disable();
    }
  }

  private isControllerInteractionEnabled(): boolean {
    return this.enabled || this.selectionCaptureActive;
  }

  private reattachControllerForCurrentMode(): void {
    const player = this.latestPlayer;
    if (!player) {
      this.applyEnabledState();
      return;
    }

    const selectionsToRestore = this.selectionSummaries.map((selection) => ({
      ...selection,
    }));

    this.teardownController({ preserveSelections: true });
    this.createPluginForCurrentMode();

    if (!this.plugin) {
      this.notify();
      return;
    }

    this.controller = this.plugin.attach(player);
    this.applyEnabledState();

    if (selectionsToRestore.length) {
      this.setSelections(selectionsToRestore, { clearExisting: true });
      return;
    }

    this.syncSelectionsFromController();
    this.notify();
    this.preservingSelectionsDuringTeardown = false;
  }

  private clearControllerInteractionState(): void {
    if (this.clearingControllerInteractionState) return;

    this.clearingControllerInteractionState = true;
    try {
      this.controller?.clearSelections?.();
      this.controller?.clearHover?.();
    } finally {
      this.clearingControllerInteractionState = false;
    }
  }

  private clearLocalInteractionState(): void {
    this.selectionSummaries = [];
    this.primarySelection = undefined;
    this.hoveredSummaries = [];
    this.hoveredPrimarySelection = undefined;
    this.programmaticHoverKey = undefined;
    this.pendingContextMenu = undefined;
    this.publishedContextMenuOpen = false;
    this.preservingSelectionsDuringTeardown = false;
    this.resetDrillThroughState();
  }

  private clearDisabledInteractionState(options?: {
    dismissContextMenu?: boolean;
  }): void {
    this.clearControllerInteractionState();
    this.clearLocalInteractionState();
    if (options?.dismissContextMenu) {
      this.requestContextMenuDismissal();
      return;
    }
    this.notify();
  }

  private teardownController(options?: { preserveSelections?: boolean }): void {
    const shouldPreserveSelections = options?.preserveSelections === true;
    const controller = this.controller;
    try {
      this.preservingSelectionsDuringTeardown = shouldPreserveSelections;
      if (controller) {
        if (typeof this.plugin?.destroy === "function") {
          this.plugin.destroy(controller);
        } else if (typeof this.plugin?.detach === "function") {
          this.plugin.detach(controller);
        } else if (typeof controller.dispose === "function") {
          controller.dispose();
        } else {
          controller.destroy?.();
          controller.detach?.();
        }
      }
    } catch (error) {
      console.warn(
        "[LayerInspectorTools] Failed to tear down controller",
        error
      );
    } finally {
      if (!shouldPreserveSelections) {
        this.preservingSelectionsDuringTeardown = false;
      }
      this.controller = null;
    }
  }

  private finishSelectionPreservation(): void {
    const finish = () => {
      this.preservingSelectionsDuringTeardown = false;
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(finish);
    } else {
      setTimeout(finish, 0);
    }
  }

  private updateSelectionSnapshots(descriptors: any[]): void {
    this.selectionSummaries = descriptors.map((descriptor) =>
      this.toSelectionSummary(descriptor)
    );
    this.primarySelection = this.selectionSummaries[
      this.selectionSummaries.length - 1
    ];
  }

  private updateHoveredSnapshots(layers: any[]): void {
    const topLayer =
      Array.isArray(layers) && layers.length
        ? this.toLayerInfoSummary(layers[0])
        : undefined;
    this.hoveredSummaries = topLayer ? [topLayer] : [];
    this.hoveredPrimarySelection = topLayer;
  }

  private buildSelectionQueries(
    selection: LayerInspectorSelection
  ): LayerSelectionQuery[] {
    const queries: LayerSelectionQuery[] = [];
    const uidClassName =
      typeof selection.layerUid === "number"
        ? buildLayerUidClassName(selection.layerUid, selection.assetName)
        : undefined;
    const nameClassName = selection.layerName
      ? buildLayerNameClassName(selection.layerName, selection.assetName)
      : undefined;
    if (uidClassName) {
      queries.push({ className: uidClassName });
    }
    if (
      selection.className &&
      selection.className !== uidClassName &&
      selection.className !== nameClassName
    ) {
      queries.push({ className: selection.className });
    }
    if (nameClassName && nameClassName !== uidClassName) {
      queries.push({ className: nameClassName });
    }
    if (selection.path) {
      queries.push({ path: selection.path });
    }
    if (selection.layerName) {
      queries.push({
        layerName: selection.layerName,
        assetName: selection.assetName,
      });
    }
    return queries;
  }

  private buildSelectionQuery(
    selection: LayerInspectorSelection
  ): LayerSelectionQuery | null {
    const [query] = this.buildSelectionQueries(selection);
    return query ?? null;
  }

  private ensureLayerSelection(layer?: LayerInspectorSelection): void {
    if (!layer || this.isLayerSelected(layer)) {
      return;
    }
    const query = this.buildSelectionQuery(layer);
    if (!query) return;
    this.controller?.select(query);
  }

  private requestContextMenuDismissal(): void {
    if (!this.publishedContextMenuOpen && !this.pendingContextMenu) {
      return;
    }

    this.pendingContextMenuDismissId = ++this.contextMenuDismissCounter;
    this.notify();
    this.pendingContextMenuDismissId = undefined;
    this.publishedContextMenuOpen = false;
  }

  private matchesSelection(
    target?: LayerInspectorSelection,
    selection?: LayerInspectorSelection
  ): boolean {
    if (!target || !selection) return false;
    if (target.instancePath || selection.instancePath) {
      return Boolean(
        target.instancePath &&
          selection.instancePath &&
          target.instancePath === selection.instancePath
      );
    }
    if (target.path && selection.path) {
      return target.path === selection.path;
    }
    if (
      typeof target.layerUid === "number" &&
      typeof selection.layerUid === "number"
    ) {
      const sameLayerUid = target.layerUid === selection.layerUid;
      if (sameLayerUid) {
        if (
          typeof target.assetUid === "number" &&
          typeof selection.assetUid === "number"
        ) {
          return target.assetUid === selection.assetUid;
        }
        return true;
      }
    }
    if (target.layerName && selection.layerName) {
      if (target.layerName !== selection.layerName) return false;
      if (target.assetName && selection.assetName) {
        return target.assetName === selection.assetName;
      }
      return true;
    }
    return false;
  }

  private isLayerSelected(layer?: LayerInspectorSelection): boolean {
    if (!layer) return false;
    return this.selectionSummaries.some((selection) =>
      this.matchesSelection(layer, selection)
    );
  }

  private syncSelectionsFromController(): void {
    const descriptors = (this.controller?.getSelections?.() as any[]) || [];
    if (
      this.preservingSelectionsDuringTeardown &&
      descriptors.length === 0 &&
      this.selectionSummaries.length > 0
    ) {
      return;
    }
    this.updateSelectionSnapshots(descriptors);
  }

  private refreshSelectionsAfterProgrammaticSelect(
    selection: LayerInspectorSelection
  ): void {
    const applySelection = () => {
      this.syncSelectionsFromController();
      if (!this.isLayerSelected(selection)) {
        this.selectionSummaries = [selection];
        this.primarySelection = selection;
      }
      this.notify();
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(applySelection);
    } else {
      setTimeout(applySelection, 0);
    }
  }

  private refreshSelectionsAfterProgrammaticMultiSelect(
    selections: LayerInspectorSelection[]
  ): void {
    const applySelection = () => {
      this.syncSelectionsFromController();
      const hasAllSelections = selections.every((selection) =>
        this.isLayerSelected(selection)
      );
      if (!hasAllSelections && selections.length) {
        this.selectionSummaries = selections;
        this.primarySelection = selections[selections.length - 1];
      }
      this.notify();
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(applySelection);
    } else {
      setTimeout(applySelection, 0);
    }
  }

  private toSelectionSummary(descriptor: any): LayerInspectorSelection {
    return this.toLayerInfoSummary(descriptor?.layer ?? descriptor ?? {});
  }

  private toLayerInfoSummary(layerInfo: any): LayerInspectorSelection {
    const info = layerInfo ?? {};
    const animationLayer = info?.layer ?? {};
    const resolvedLayerUid =
      typeof animationLayer?.uid === "number"
        ? animationLayer.uid
        : typeof info.layerUid === "number"
        ? info.layerUid
        : typeof info.uid === "number"
        ? info.uid
        : undefined;
    const resolvedLayerName =
      typeof info.layerName === "string" && info.layerName.length > 0
        ? info.layerName
        : typeof animationLayer?.nm === "string" && animationLayer.nm.length > 0
        ? animationLayer.nm
        : undefined;
    const resolvedLayerType =
      typeof animationLayer?.ty === "number"
        ? animationLayer.ty
        : typeof info.layerType === "number"
        ? info.layerType
        : undefined;
    const resolvedAssetName =
      typeof info.assetName === "string" && info.assetName.length > 0
        ? info.assetName
        : typeof info.asset?.nm === "string" && info.asset.nm.length > 0
        ? info.asset.nm
        : typeof info.asset?.id === "string"
        ? info.asset.id
        : undefined;
    const resolvedAssetUid =
      typeof info.assetUid === "number"
        ? info.assetUid
        : typeof info.asset?.uid === "number"
        ? info.asset.uid
        : undefined;
    return {
      path: info.path ?? info.layerPath,
      instancePath:
        typeof info.instancePath === "string" && info.instancePath.length > 0
          ? info.instancePath
          : undefined,
      className: info.className,
      assetName: resolvedAssetName,
      layerName: resolvedLayerName,
      assetUid: resolvedAssetUid,
      layerUid: resolvedLayerUid,
      layerType: resolvedLayerType,
      refId:
        typeof animationLayer?.refId === "string"
          ? animationLayer.refId
          : undefined,
      // assetUid: info.asset
    };
  }

  private notify(): void {
    const startedAt = studioPerfNow();
    const snapshot = this.getState();
    const previousSnapshot = this.lastNotifiedState;
    const subscriberCount = this.subscribers.size;
    if (
      previousSnapshot &&
      areLayerInspectorStatesEqual(previousSnapshot, snapshot)
    ) {
      recordStudioPerf(
        "LayerInspectorTools.notify skipped-same",
        studioPerfNow() - startedAt,
        {
          hovered: snapshot.hoveredLayers.length,
          mode: snapshot.mode,
          nextHoverKey: getLayerInspectorSelectionDebugKey(
            snapshot.hoveredPrimarySelection
          ),
          selections: snapshot.selections.length,
          subscribers: subscriberCount,
        }
      );
      return;
    }

    this.lastNotifiedState = snapshot;
    this.subscribers.forEach((subscriber, cb) => {
      const subscriberStartedAt = studioPerfNow();
      cb(snapshot);
      recordStudioPerf(
        `LayerInspectorTools.notify subscriber ${subscriber.label}`,
        studioPerfNow() - subscriberStartedAt,
        {
          hovered: snapshot.hoveredLayers.length,
          mode: snapshot.mode,
          selections: snapshot.selections.length,
          source: subscriber.source,
        }
      );
    });
    recordStudioPerf(
      "LayerInspectorTools.notify",
      studioPerfNow() - startedAt,
      {
        diff: getLayerInspectorStateDiff(previousSnapshot, snapshot),
        hovered: snapshot.hoveredLayers.length,
        mode: snapshot.mode,
        nextHoverKey: getLayerInspectorSelectionDebugKey(
          snapshot.hoveredPrimarySelection
        ),
        previousHoverKey: getLayerInspectorSelectionDebugKey(
          previousSnapshot?.hoveredPrimarySelection
        ),
        selections: snapshot.selections.length,
        subscribers: subscriberCount,
      }
    );
  }

  private handleRightClick(
    layer: unknown,
    selections: unknown[] | undefined,
    event?: MouseEvent
  ): void {
    if (!this.enabled || this.mode === "view") {
      this.clearDisabledInteractionState({ dismissContextMenu: true });
      return;
    }

    if (!layer) {
      this.requestContextMenuDismissal();
      return;
    }
    const summary = this.toLayerInfoSummary(layer);
    const selectionSummaries = Array.isArray(selections)
      ? selections.map((selection) => this.toSelectionSummary(selection))
      : [];

    if (selectionSummaries.length) {
      this.selectionSummaries = selectionSummaries;
      this.primarySelection = selectionSummaries[selectionSummaries.length - 1];
    }

    if (!this.isLayerSelected(summary)) {
      this.controller?.clearSelections?.();
      this.selectionSummaries = [];
      this.primarySelection = undefined;
      this.ensureLayerSelection(summary);
    }
    this.pendingContextMenu = {
      id: ++this.contextMenuCounter,
      position: this.extractClickPosition(event),
      layer: summary,
      selections: this.selectionSummaries.length
        ? [...this.selectionSummaries]
        : [summary],
    };
    this.notify();
    this.pendingContextMenu = undefined;
    this.publishedContextMenuOpen = true;
  }

  private handleDoubleClick(
    layer: unknown,
    selections: unknown[] | undefined
  ): void {
    if (!this.enabled || this.mode !== "connect") {
      return;
    }

    if (!layer) {
      return;
    }

    const summary = this.toLayerInfoSummary(layer);
    const selectionSummaries = Array.isArray(selections)
      ? selections.map((selection) => this.toSelectionSummary(selection))
      : [];

    if (selectionSummaries.length) {
      this.selectionSummaries = selectionSummaries;
      this.primarySelection = selectionSummaries[selectionSummaries.length - 1];
    }

    if (!this.isLayerSelected(summary)) {
      this.controller?.clearSelections?.();
      this.selectionSummaries = [];
      this.primarySelection = undefined;
      this.ensureLayerSelection(summary);
    }

    this.layerDoubleClickHandler?.(summary);
    this.requestContextMenuDismissal();
    this.notify();
  }

  private extractClickPosition(event?: MouseEvent) {
    return {
      clientX: event?.clientX ?? 0,
      clientY: event?.clientY ?? 0,
      pageX: event?.pageX ?? 0,
      pageY: event?.pageY ?? 0,
    };
  }

  private resetDrillThroughState(): void {
    this.drillThroughLayers = [];
    this.drillThroughIndex = 0;
    this.lastDrillClickPosition = null;
  }

  private handleDrillThroughClick(
    selection: unknown,
    _selections: unknown[] | undefined,
    event: MouseEvent
  ): void {
    const clickPosition = { x: event.clientX, y: event.clientY };
    const clickedSummary = this.toSelectionSummary(selection);

    // Check if this is a new click position (different from last drill-through)
    const isNewPosition =
      !this.lastDrillClickPosition ||
      Math.abs(this.lastDrillClickPosition.x - clickPosition.x) > 5 ||
      Math.abs(this.lastDrillClickPosition.y - clickPosition.y) > 5;

    if (isNewPosition) {
      // New position: gather all layers at this point
      this.lastDrillClickPosition = clickPosition;
      this.drillThroughLayers = this.findLayersAtPoint(event);
      this.drillThroughIndex = 0;

      // If we found layers, select the first one (topmost)
      if (this.drillThroughLayers.length > 0) {
        this.selectDrillThroughLayer(this.drillThroughIndex);
      } else {
        // Fallback: just select the clicked layer
        this.controller?.clearSelections?.();
        const query = this.buildSelectionQuery(clickedSummary);
        if (query) {
          this.controller?.select?.(query);
          this.refreshSelectionsAfterProgrammaticSelect(clickedSummary);
        }
      }
    } else {
      // Same position: cycle to next layer
      if (this.drillThroughLayers.length > 1) {
        this.drillThroughIndex =
          (this.drillThroughIndex + 1) % this.drillThroughLayers.length;
        this.selectDrillThroughLayer(this.drillThroughIndex);
      }
    }

    this.requestContextMenuDismissal();
    this.notify();
  }

  private selectDrillThroughLayer(index: number): void {
    const layer = this.drillThroughLayers[index];
    if (!layer) return;

    this.controller?.clearSelections?.();
    const query = this.buildSelectionQuery(layer);
    if (query) {
      this.controller?.select?.(query);
      this.selectionSummaries = [layer];
      this.primarySelection = layer;
    }
  }

  private findLayersAtPoint(event: MouseEvent): LayerInspectorSelection[] {
    const layers: LayerInspectorSelection[] = [];
    const point = { x: event.clientX, y: event.clientY };

    // Find all layer selector elements at this point
    const elements = document.elementsFromPoint(point.x, point.y);

    for (const el of elements) {
      // Check if this element or any ancestor has bl_edit_selector class
      const layerEl = el.closest('[class*="bl_edit_selector"]') as HTMLElement;
      if (!layerEl) continue;

      const classAttr = layerEl.getAttribute("class") || "";
      const selectorClass = classAttr
        .split(/\s+/)
        .find((cls) => cls.includes("bl_edit_selector"));

      if (selectorClass) {
        // Parse the class to extract layer info
        const layerInfo = this.parseLayerClassName(selectorClass);
        if (layerInfo) {
          // Avoid duplicates
          const isDuplicate = layers.some(
            (l) =>
              l.className === layerInfo.className ||
              (typeof l.layerUid === "number" &&
                l.layerUid === layerInfo.layerUid)
          );
          if (!isDuplicate) {
            layers.push(layerInfo);
          }
        }
      }
    }

    return layers;
  }

  private parseLayerClassName(
    className: string
  ): LayerInspectorSelection | null {
    // Parse bl_edit_selector-$a_<asset>_$l_uid_<uid> format
    // or bl_edit_selector-$l_uid_<uid> format
    const uidMatch = className.match(/\$l_uid_(\d+)/);
    const assetMatch = className.match(/\$a_([^_$]+)/);

    if (uidMatch) {
      const layerUid = parseInt(uidMatch[1], 10);
      const assetName = assetMatch ? assetMatch[1] : undefined;

      const parsed = {
        path: "",
        className,
        layerUid,
        assetName,
        layerName: `uid_${layerUid}`,
      };
      return parsed;
    }

    // Try parsing layer name format: bl_edit_selector-$a_<asset>_$l_<name>
    const nameMatch = className.match(/\$l_([^_$]+)$/);
    if (nameMatch) {
      const layerName = nameMatch[1];
      const assetName = assetMatch ? assetMatch[1] : undefined;

      const parsed = {
        path: "",
        className,
        layerName,
        assetName,
      };
      return parsed;
    }

    return null;
  }

  private findLayerClassNamesByUid(layerUid: number): string[] {
    const uidToken = `$l_uid_${layerUid}`;
    const matches = new Set<string>();
    const elements = Array.from(
      document.querySelectorAll(
        `[class*="bl_edit_selector"][class*="${uidToken}"]`
      )
    );

    elements.forEach((el) => {
      const classAttr = el.getAttribute("class") || "";
      classAttr.split(/\s+/).forEach((cls) => {
        if (cls.includes("bl_edit_selector") && cls.includes(uidToken)) {
          matches.add(cls);
        }
      });
    });

    return Array.from(matches);
  }
}

const layerInspectorTools = new LayerInspectorTools();

export { buildLayerUidClassName, buildLayerNameClassName };

export const installLayerInspector = (params: ISdkParams): Promise<void> =>
  layerInspectorTools.install(params);
export const attachLayerInspector = (player: Player) =>
  layerInspectorTools.attach(player);
export const detachLayerInspector = (): void => layerInspectorTools.detach();
export const detachLayerInspectorForPlayer = (player?: Player | null): void =>
  layerInspectorTools.detachForPlayer(player);
export const setLayerInspectorEnabled = (enabled: boolean): void =>
  layerInspectorTools.setEnabled(enabled);
export const setLayerInspectorSelectionCaptureEnabled = (
  enabled: boolean
): void => layerInspectorTools.setSelectionCaptureEnabled(enabled);
export const toggleLayerInspector = (): void => layerInspectorTools.toggle();
export const setLayerInspectorMode = (mode: LayerInspectorMode): void =>
  layerInspectorTools.setMode(mode);
export const getLayerInspectorMode = (): LayerInspectorMode =>
  layerInspectorTools.getMode();
export const isLayerInspectorEnabled = (): boolean =>
  layerInspectorTools.isEnabled();
export const isLayerInspectorAvailable = (): boolean =>
  layerInspectorTools.isAvailable();
export const subscribeToLayerInspector = (
  cb: (state: LayerInspectorState) => void,
  debugLabel?: string
): (() => void) => layerInspectorTools.subscribe(cb, debugLabel);
export const getLayerInspectorState = (): LayerInspectorState =>
  layerInspectorTools.getState();
export const selectLayerInInspector = (
  layerName: string,
  assetName?: string
): void => layerInspectorTools.selectLayer(layerName, assetName);
export const selectLayerByUidInInspector = (
  layerUid: number,
  assetId?: string,
  assetName?: string,
  layerNm?: string,
  layerPath?: string
): void =>
  layerInspectorTools.selectLayerByUid(
    layerUid,
    assetId,
    assetName,
    layerNm,
    layerPath
  );
export const setLayerInspectorSelections = (
  selections: LayerInspectorSelection[],
  options?: { clearExisting?: boolean }
): void => layerInspectorTools.setSelections(selections, options);
export const clearLayerInspectorSelections = (): void =>
  layerInspectorTools.clearSelections();
export const setLayerInspectorHover = (
  selection?: LayerInspectorSelection
): void => layerInspectorTools.setHover(selection);
export const clearLayerInspectorHover = (): void =>
  layerInspectorTools.clearHover();
export const getLayerInspectorHover = (): LayerInspectorSelection | undefined =>
  layerInspectorTools.getHover();
export const setLayerInspectorTransformCommitHandler = (
  handler?: LayerInspectorTransformCommitHandler
): void => layerInspectorTools.setTransformCommitHandler(handler);
export const setLayerInspectorTextControlReportHandler = (
  handler?: LayerInspectorTextControlReportHandler
): void => layerInspectorTools.setTextControlReportHandler(handler);
export const setLayerInspectorTextEditCommitHandler = (
  handler?: LayerInspectorTextEditCommitHandler
): void => layerInspectorTools.setTextEditCommitHandler(handler);
export const setLayerInspectorTextConnectorActionHandler = (
  handler?: LayerInspectorTextConnectorActionHandler
): void => layerInspectorTools.setTextConnectorActionHandler(handler);
export const setLayerInspectorLayerDoubleClickHandler = (
  handler?: LayerInspectorLayerDoubleClickHandler
): void => layerInspectorTools.setLayerDoubleClickHandler(handler);

export default layerInspectorTools;
